import React from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateFormatter';
import { getMockupUrl } from '../utils/fileHelpers';
import { getOrderStatusDisplay } from '../firebase/schema';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
`;

const OrderRow = styled.div`
  background: var(--al-surface);
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const OrderInfo = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const ReorderButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
  padding: 8px 16px;
  text-transform: uppercase;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.05em;

  &:hover {
    background: #7ACC7A;
  }
`;

const LinesContainer = styled.div`
  padding: 16px;
`;

const LineItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 150px 150px 150px auto;
  gap: 16px;
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  align-items: center;
`;

const ArtworkThumb = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  background: #3C3C3C;
`;

const SizesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
  gap: 4px;
`;

const SizeBox = styled.div`
  background: #3C3C3C;
  padding: 4px;
  text-align: center;
  font-size: 0.8em;

  .size {
    font-weight: bold;
    color: ${props => props.theme.colors.primary};
  }
  
  .quantity {
    color: white;
  }
`;

const CustomerOrdersModal = ({ isOpen, onClose, orders, customerName }) => {
  const navigate = useNavigate();

  const handleReorder = (order) => {
    const today = new Date();
    navigate('/orders/new', {
      state: {
        reorderData: {
          ...order,
          startDate: today,
          dueDate: today,
          isReorder: true,
          originalOrderId: order.id
        }
      }
    });
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <h2>{customerName}'s Orders</h2>

        {orders.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: 24 }}>
            No orders found for this customer.
          </p>
        ) : orders.map(order => (
          <OrderRow key={order.id}>
            <OrderHeader>
              <OrderInfo>
                <div>PO: {order.poNumber || order.orderNumber || order.id?.slice(0, 8)}</div>
                <div>Due Date: {formatDate(order.dueDate)}</div>
                <div>Total Quantity: {order.totalQuantity}</div>
                <div>Invoice Total: ${Number(order.invoiceTotal || order.orderTotal || 0).toFixed(2)}</div>
                <div>Status: {getOrderStatusDisplay(order)}</div>
              </OrderInfo>
              <ReorderButton onClick={() => handleReorder(order)}>
                Create Reorder
              </ReorderButton>
            </OrderHeader>
            
            <LinesContainer>
              {order.lines?.map(line => (
                <LineItem key={line.id}>
                  <ArtworkThumb 
                    src={getMockupUrl(line) || '/placeholder.png'} 
                    alt={line.designName}
                  />
                  <div>
                    <div><strong>{line.designName}</strong></div>
                    <div>{line.garmentId} - {line.color}</div>
                    <div>${line.pricePerPiece} per piece</div>
                  </div>
                  <SizesGrid>
                    {Object.entries(line.sizes || {}).map(([size, qty]) => (
                      qty > 0 && (
                        <SizeBox key={size}>
                          <div className="size">{size}</div>
                          <div className="quantity">{qty}</div>
                        </SizeBox>
                      )
                    ))}
                  </SizesGrid>
                </LineItem>
              ))}
            </LinesContainer>
          </OrderRow>
        ))}
      </ModalContent>
    </Modal>
  );
};

export default CustomerOrdersModal; 